import { AsyncLocalStorage } from "async_hooks";
import { NextApiRequest, NextApiResponse } from "next";
import { getBrowserUser } from "#/lib/supabase/utils";
import { HttpStatusCode } from "#/utils/http-status-code";
import { User } from "@supabase/gotrue-js";
import { Logger } from "#/lib/logging";
import { isEdgeRuntime, isNextJSServer } from "#/utils/environment";
import { withResult } from "ts-error-as-value";

interface RequestContextNamespace {
  userId: string
}
export let requestContextStorage: AsyncLocalStorage<RequestContextNamespace> = null as any;

if (isNextJSServer() && !isEdgeRuntime()) {
  requestContextStorage = new AsyncLocalStorage<RequestContextNamespace>();
}

interface RunRequestContextStorageParams<R> {
  userId: string,
  cb: () => Promise<R>
}

class UserNotAuthenticatedError extends Error {
  constructor() {
    super("User not authenticated");
    this.name = "UserNotAuthenticatedError";
  }
}

export const runRequestContextStorage = async <R>({
  userId,
  cb
}: RunRequestContextStorageParams<R>): Promise<R> => {
  try {
    const result = await new Promise<R>((resolve, reject) => {
      requestContextStorage.run({
        userId
      }, cb)
        .then(resolve)
        .catch(reject);
    });
    return result;
  } catch (e) {
    throw e as Error;
  }
};

export const userRequestContextWrapper = (
  handler: (req: NextApiRequest, res: NextApiResponse, user: User) => Promise<void | NextApiResponse>
) => async (
  req: NextApiRequest, res: NextApiResponse
): Promise<void | NextApiResponse> => {
  // req.headers
  const user = await getBrowserUser({ req, res });
  if (!user) {
    return res.status(HttpStatusCode.UNAUTHORIZED).json({ error: "User not found" });
  }

  const {
    data: result, error
  } = await withResult(runRequestContextStorage)({
    userId: user.id,
    cb: async () => {
      try {
        return handler(req, res, user);
      } catch (e) {
        Logger.error({
          error: e as Error,
          message: "Error caught in request context wrapper"
        });
        return res.status(HttpStatusCode.INTERNAL_SERVER_ERROR).json({ error: "Internal server error" });
      }
    }
  });

  if (error) {
    if (error instanceof UserNotAuthenticatedError) {
      return res.status(HttpStatusCode.UNAUTHORIZED).json({ error: "User not authenticated" });
    }
    Logger.error({
      error,
      message: "Error caught in request context wrapper"
    });
    return res.status(HttpStatusCode.INTERNAL_SERVER_ERROR).json({ error: error.message });
  }

  return result;
};